import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BellRing,
  ChevronRight,
  ClipboardList,
  FileText,
  HeartPulse,
  Quote,
  Shield,
  Utensils,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useLang } from "../../context/LangContext";
import { getV2Copy } from "../content";

export default function V2Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, lang, setLang } = useLang();
  const copy = getV2Copy(lang);

  const features = [
    { icon: Utensils, label: t("feat_tracking") },
    { icon: Activity, label: t("feat_monitor") },
    { icon: ClipboardList, label: t("feat_summary") },
    { icon: BellRing, label: t("feat_reminders") },
    { icon: BarChart3, label: t("feat_notifs") },
    { icon: FileText, label: t("feat_reports") },
  ];

  const steps = [
    { n: "01", title: t("step1_title"), desc: t("step1_desc"), icon: Utensils },
    { n: "02", title: t("step2_title"), desc: t("step2_desc"), icon: Activity },
    { n: "03", title: t("step3_title"), desc: t("step3_desc"), icon: HeartPulse },
  ];

  const stories = [
    { name: t("story1_name"), quote: t("story1_quote") },
    { name: t("story2_name"), quote: t("story2_quote") },
    { name: t("story3_name"), quote: t("story3_quote") },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:px-8">
          <Link to="/" className="flex items-center gap-3 text-lg font-bold">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white">
              <HeartPulse className="h-5 w-5" />
            </div>
            Dialysis Diet Pro
          </Link>

          <nav className="hidden items-center gap-6 text-sm md:flex">
            <a href="#how" className="hover:text-primary">How It Works</a>
            <a href="#features" className="hover:text-primary">Features</a>
            <a href="#stories" className="hover:text-primary">Stories</a>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="rounded-md border border-border px-2 py-1 text-sm"
            >
              {lang === "ar" ? "EN" : "AR"}
            </button>
            <Link to="/login" className="hidden text-sm sm:inline">{copy.login}</Link>
            <Link to="/register" className="hidden sm:inline">
              <Button size="sm">{copy.register}</Button>
            </Link>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="rounded-lg border px-3 py-2 md:hidden"
              aria-label="Open menu"
            >
              Menu
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="flex flex-col gap-3 px-4 pb-4 text-sm md:hidden">
            <a href="#how">How It Works</a>
            <a href="#features">Features</a>
            <a href="#stories">Stories</a>
            <Link to="/login">{copy.login}</Link>
            <Link to="/register">{copy.register}</Link>
            <Link to="/guest">{copy.guest}</Link>
          </div>
        )}
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 md:px-8 md:pb-28 md:pt-24">
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
              <div className="fade-up">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">
                  <Shield className="h-3.5 w-3.5" />
                  Professional Dialysis Nutrition Tracker
                </div>
                <h1
                  className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl"
                  style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}
                  data-testid="hero-title"
                >
                  {t("tagline")}
                </h1>
                <p className="mt-6 max-w-xl text-lg text-muted-foreground">{t("subtagline")}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/register" className="w-full sm:w-auto">
                    <Button size="lg" className="btn-primary h-12 w-full rounded-full px-6 sm:w-auto" data-testid="hero-get-started">
                      {copy.register}
                      <ChevronRight className="ms-1 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/login" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="h-12 w-full rounded-full px-6 sm:w-auto" data-testid="hero-login">
                      {copy.login}
                    </Button>
                  </Link>
                  <Link to="/guest" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="h-12 w-full rounded-full px-6 sm:w-auto">
                      {copy.guest}
                    </Button>
                  </Link>
                </div>
                <div className="mt-10 grid grid-cols-4 gap-4 text-center">
                  {["potassium", "phosphorus", "sodium", "protein"].map((key) => (
                    <div key={key} className="bento-card flex h-[70px] flex-col justify-between !p-3">
                      <div className="truncate text-[10px] uppercase tracking-widest text-muted-foreground sm:text-xs">
                        {t(key)}
                      </div>
                      <div className="mt-1 h-1.5 rounded-full" style={{ background: `var(--rs-${key})` }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl opacity-30 blur-3xl" style={{ background: "var(--rs-primary)" }} />
                <img
                  src="https://images.pexels.com/photos/8413336/pexels-photo-8413336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Nurse and senior patient"
                  className="relative aspect-[4/3] w-full rounded-3xl border border-border object-cover"
                  data-testid="hero-image"
                />
                <div className="bento-card absolute bottom-4 start-4 flex max-w-[240px] items-center gap-3 !p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "var(--rs-sodium)" }}>
                    <HeartPulse className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">
                      {lang === "ar" ? "حمية اليوم لمرضى غسيل الكلى" : "Today's Dialysis Diet"}
                    </div>
                    <div className="text-sm font-semibold">
                      {lang === "ar" ? "العناصر الغذائية ضمن المعدل الآمن" : "Nutrients within safe range"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how" className="border-t border-border bg-muted/30 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
            <h2
              className="text-3xl font-extrabold tracking-tight md:text-4xl"
              style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}
            >
              {t("how_it_works")}
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.n} className="bento-card hover:-translate-y-1">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{step.n}</span>
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "var(--rs-primary)" }}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                    </div>
                    <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="features" className="border-t border-border py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
            <h2
              className="text-3xl font-extrabold tracking-tight md:text-4xl"
              style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}
            >
              {t("features")}
            </h2>
            <div className="mt-10 grid grid-cols-2 gap-6 md:grid-cols-3">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.label}
                    className="bento-card flex h-[100px] flex-col items-center justify-center gap-3 text-center md:h-auto md:min-h-[112px]"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--rs-sodium)22" }}>
                      <Icon className="h-5 w-5" style={{ color: "var(--rs-primary)" }} />
                    </div>
                    <div className="max-w-[9rem] whitespace-normal text-[13.5px] font-bold leading-[1.25] text-foreground sm:text-sm">
                      {feature.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="stories" className="border-t border-border bg-muted/30 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
            <div className="grid items-start gap-12 md:grid-cols-2">
              <div>
                <h2
                  className="text-3xl font-extrabold tracking-tight md:text-4xl"
                  style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}
                >
                  {t("patient_stories")}
                </h2>
                <img
                  src="https://images.pexels.com/photos/7345460/pexels-photo-7345460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Healthcare professional with elderly man"
                  className="mt-8 aspect-[4/3] w-full rounded-3xl border border-border object-cover"
                />
              </div>
              <div className="space-y-4">
                {stories.map((story) => (
                  <div key={story.name} className="bento-card">
                    <Quote className="mb-3 h-6 w-6 text-muted-foreground" />
                    <p className="text-lg leading-relaxed">&ldquo;{story.quote}&rdquo;</p>
                    <div className="mt-4 text-sm font-semibold" style={{ color: "var(--rs-primary)" }}>
                      - {story.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-muted/30 py-16">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 md:px-8">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "var(--rs-sodium)22" }}>
              <Shield className="h-6 w-6" style={{ color: "var(--rs-primary)" }} />
            </div>
            <p className="mx-auto max-w-3xl text-lg text-muted-foreground" data-testid="disclaimer-text">
              {copy.guidance}
            </p>
          </div>
        </section>

        <section className="border-t border-border py-12 text-center">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 md:px-8">
            <p className="mb-3 text-xs uppercase tracking-wider text-teal-400">
              {lang === "ar" ? "شكر خاص" : "Special Thanks"}
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground md:text-base">
              {lang === "ar" ? (
                <>شكر خاص إلى <span className="font-medium text-teal-400/90">مريض غسيل الكلى المهندس أنطوان بعقليني</span> على دعمه وثقته ومساهمته القيّمة في تطوير Dialysis Diet Pro.</>
              ) : (
                <>Special thanks to <span className="font-medium text-teal-400/90">Dialysis Patient Eng. Antoine Baaklini</span> for his support, trust, and valuable contribution to Dialysis Diet Pro.</>
              )}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-sm text-muted-foreground sm:px-6 md:flex-row md:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
            <HeartPulse className="h-4 w-4" />
            <span className="font-semibold">Dialysis Diet Pro</span>
            <span>-</span>
            <span>{t("copyright")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>{t("powered_by")}</span>
          </div>
        </div>
        <nav aria-label="Legal pages" className="mx-auto mt-4 flex max-w-7xl flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm text-muted-foreground sm:px-6 md:px-8">
          <Link to="/terms" className="hover:text-primary">
            {lang === "ar" ? "شروط الاستخدام" : "Terms of Use"}
          </Link>
          <Link to="/privacy" className="hover:text-primary">
            {lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}
          </Link>
          <Link to="/medical-disclaimer" className="hover:text-primary">
            {lang === "ar" ? "إخلاء المسؤولية الطبية" : "Medical Disclaimer"}
          </Link>
        </nav>
      </footer>
    </div>
  );
}
